// React hook for dog pose detection
import { useState, useEffect, useCallback, useRef } from "react";
import {
  getDogDetector,
  resetDogDetector,
  DogDetection,
  DogDetector,
} from "../services/dogDetector";

export interface UseDogDetectorOptions {
  modelPath?: string;
  autoLoad?: boolean;
  scoreThreshold?: number;
  iouThreshold?: number;
  minKeypointConfidence?: number;
}

export interface UseDogDetectorResult {
  isLoaded: boolean;
  isLoading: boolean;
  error: Error | null;
  detectFromImage: (
    image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
  ) => Promise<DogDetection | null>;
  detectFromUrl: (imageUrl: string) => Promise<DogDetection | null>;
  lastDetection: DogDetection | null;
  inferenceTime: number;
}

export function useDogDetector(
  options: UseDogDetectorOptions = {},
): UseDogDetectorResult {
  const {
    modelPath = "/models/dog-pose-50-epoch-best-yolo11_web_model/model.json",
    autoLoad = true,
    scoreThreshold,
    iouThreshold,
    minKeypointConfidence,
  } = options;

  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastDetection, setLastDetection] = useState<DogDetection | null>(null);
  const [inferenceTime, setInferenceTime] = useState(0);

  const detectorRef = useRef<DogDetector | null>(null);

  // Load detector
  useEffect(() => {
    if (!autoLoad) return;

    const loadDetector = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const detector = getDogDetector(modelPath);
        await detector.load();
        // Apply options after load
        if (scoreThreshold !== undefined)
          detector.setScoreThreshold?.(scoreThreshold);
        if (iouThreshold !== undefined)
          detector.setIouThreshold?.(iouThreshold);
        if (minKeypointConfidence !== undefined)
          detector.setMinKeypointConfidence?.(minKeypointConfidence);
        detectorRef.current = detector;
        setIsLoaded(true);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    loadDetector();

    return () => {
      // Cleanup on unmount
      resetDogDetector();
      detectorRef.current = null;
    };
  }, [
    modelPath,
    autoLoad,
    scoreThreshold,
    iouThreshold,
    minKeypointConfidence,
  ]);

  const detectFromImage = useCallback(
    async (
      image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
    ): Promise<DogDetection | null> => {
      if (!detectorRef.current) {
        throw new Error("Detector not loaded");
      }

      const startTime = performance.now();
      try {
        const result = await detectorRef.current.detectFromImage(image);
        const endTime = performance.now();
        setInferenceTime(endTime - startTime);

        if (result) {
          setLastDetection(result);
        }

        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      }
    },
    [],
  );

  const detectFromUrl = useCallback(
    async (imageUrl: string): Promise<DogDetection | null> => {
      if (!detectorRef.current) {
        throw new Error("Detector not loaded");
      }

      const startTime = performance.now();
      try {
        const result = await detectorRef.current.detectFromUrl(imageUrl);
        const endTime = performance.now();
        setInferenceTime(endTime - startTime);

        if (result) {
          setLastDetection(result);
        }

        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      }
    },
    [],
  );

  return {
    isLoaded,
    isLoading,
    error,
    detectFromImage,
    detectFromUrl,
    lastDetection,
    inferenceTime,
  };
}

// Hook for turning detection (tracks direction sequence to detect spinning)
// Direction order: right, down, left, up (clockwise)

const DIRECTIONS = ["right", "down", "left", "up"] as const;
type Direction = (typeof DIRECTIONS)[number];

// Map 8-direction facing to 4-direction (center quadrant spans 90 degrees)
function toFourDirection(eightDirection: string): string {
  const mapping: Record<string, Direction> = {
    right: "right",
    "down-right": "right",
    down: "down",
    "down-left": "down",
    left: "left",
    "up-left": "left",
    up: "up",
    "up-right": "up",
  };
  return mapping[eightDirection] || eightDirection;
}

// Get direction index (0-3)
function getDirectionIndex(direction: string): number {
  return DIRECTIONS.indexOf(direction as Direction);
}

// Calculate how many steps clockwise between two directions (-1 to 2)
function clockwiseSteps(from: number, to: number): number {
  if (from === -1 || to === -1) return 0;
  return (to - from + 4) % 4;
}

// Calculate how many steps counter-clockwise between two directions (0-2)
function counterClockwiseSteps(from: number, to: number): number {
  if (from === -1 || to === -1) return 0;
  return (from - to + 4) % 4;
}

export interface TurningState {
  directionHistory: Array<{
    direction: string;
    index: number;
    timestamp: number;
  }>;
  clockwiseAccum: number; // Accumulated clockwise rotation (in direction steps)
  counterClockwiseAccum: number; // Accumulated counter-clockwise rotation
  netRotation: number; // Net rotation (positive = clockwise, negative = counter-clockwise, in steps)
  isTurning: boolean; // Currently in a turning motion
  isSpinning: boolean; // Completed multiple rotations
  turnsCount: number; // Number of full rotations detected
  lastDirection: string;
  rotationDirection: "clockwise" | "counter-clockwise" | "none";
}

export function useTurningDetection(
  historyWindowSize: number = 5000,
  resetAfter: number = 3000,
) {
  const [turningState, setTurningState] = useState<TurningState>({
    directionHistory: [],
    clockwiseAccum: 0,
    counterClockwiseAccum: 0,
    netRotation: 0,
    isTurning: false,
    isSpinning: false,
    turnsCount: 0,
    lastDirection: "",
    rotationDirection: "none",
  });

  const lastResetTime = useRef(Date.now());

  const addDetection = useCallback(
    (detection: DogDetection, timestamp: number = Date.now()) => {
      if (!detection.facing || !detection.isValid) return;

      // Convert to 4-direction
      const direction = toFourDirection(detection.facing.direction);
      const directionIndex = getDirectionIndex(direction);
      if (directionIndex === -1) return;

      setTurningState((prev) => {
        // Add to history
        const newHistory = [
          ...prev.directionHistory,
          {
            direction,
            index: directionIndex,
            timestamp,
          },
        ];

        // Trim old history
        const cutoff = timestamp - historyWindowSize;
        while (newHistory.length > 1 && newHistory[0].timestamp < cutoff) {
          newHistory.shift();
        }

        // Calculate rotation from last direction
        let clockwiseAccum = prev.clockwiseAccum;
        let counterClockwiseAccum = prev.counterClockwiseAccum;
        let netRotation = prev.netRotation;
        let turnsCount = prev.turnsCount;
        let isTurning = prev.isTurning;
        let rotationDirection = prev.rotationDirection;

        if (prev.directionHistory.length > 0) {
          const lastEntry =
            prev.directionHistory[prev.directionHistory.length - 1];
          const lastIdx = lastEntry.index;

          // Calculate steps in each direction
          const cw = clockwiseSteps(lastIdx, directionIndex);
          const ccw = counterClockwiseSteps(lastIdx, directionIndex);

          // Only count steps of 1 (adjacent direction change)
          // Step of 2 is opposite direction (180 turn) - reset accumulators
          if (cw === 1) {
            // Clockwise movement (right → down → left → up)
            clockwiseAccum += 1;
            netRotation += 1;
            rotationDirection = "clockwise";
          } else if (ccw === 1) {
            // Counter-clockwise movement (right → up → left → down)
            counterClockwiseAccum += 1;
            netRotation -= 1;
            rotationDirection = "counter-clockwise";
          } else {
            // Opposite direction or same direction - reset accumulators
            clockwiseAccum = 0;
            counterClockwiseAccum = 0;
            if (prev.netRotation !== 0) {
              lastResetTime.current = timestamp;
            }
          }
        }

        // A full rotation = 4 direction steps (90° each)
        const fullRotation = 4;

        // Count turns: every 4 accumulated steps = 1 full rotation
        if (clockwiseAccum >= fullRotation) {
          turnsCount += Math.floor(clockwiseAccum / fullRotation);
          clockwiseAccum = clockwiseAccum % fullRotation;
        } else if (counterClockwiseAccum >= fullRotation) {
          turnsCount += Math.floor(counterClockwiseAccum / fullRotation);
          counterClockwiseAccum = counterClockwiseAccum % fullRotation;
        }

        // Detect turning: consistent movement in same direction
        isTurning =
          newHistory.length >= 3 &&
          (clockwiseAccum >= 2 || counterClockwiseAccum >= 2);

        // Spinning: completed at least one full rotation
        const isSpinning = turnsCount > 0;

        // Decay if not turning
        if (!isTurning && prev.directionHistory.length > 0) {
          const timeSinceLastReset = timestamp - lastResetTime.current;
          if (timeSinceLastReset > resetAfter) {
            clockwiseAccum = Math.max(0, clockwiseAccum - 1);
            counterClockwiseAccum = Math.max(0, counterClockwiseAccum - 1);
            if (clockwiseAccum === 0 && counterClockwiseAccum === 0) {
              rotationDirection = "none";
            }
          }
        }

        return {
          directionHistory: newHistory.slice(-20), // Keep last 20 entries max
          clockwiseAccum,
          counterClockwiseAccum,
          netRotation,
          isTurning,
          isSpinning,
          turnsCount,
          lastDirection: direction,
          rotationDirection,
        };
      });
    },
    [historyWindowSize, resetAfter],
  );

  const reset = useCallback(() => {
    setTurningState({
      directionHistory: [],
      clockwiseAccum: 0,
      counterClockwiseAccum: 0,
      netRotation: 0,
      isTurning: false,
      isSpinning: false,
      turnsCount: 0,
      lastDirection: "",
      rotationDirection: "none",
    });
    lastResetTime.current = Date.now();
  }, []);

  return {
    turningState,
    addDetection,
    reset,
  };
}
